import Button from 'components/Button';
import MyCalendar from 'components/MyCalendar';
import MyImage from 'components/MyImage';
import { Box, HStack, VStack } from 'components/Stack';
import Text from 'components/Text';
import Header from 'container/common/Header';
import 'react-calendar/dist/Calendar.css';
import { useNavigate } from 'react-router';
import styled, { useTheme } from 'styled-components';

import SeatPositions from 'assets/images/farmersbean_structure.webp';
import UserImage from 'assets/images/user.webp';
import SimpleSeat from 'components/SimpleSeat';
import Spacing from 'components/Spacing';
import BreadHello from 'container/bread/BreadHello';
import { LanguagePicker } from 'container/common/LanguagePicker';
import { useBookStatus } from 'features/book/book.hook';
import { useSeat } from 'hooks/useChangeSeat';
import { useStepAuthenticateManager } from 'hooks/useStepManager';
import { useToast } from 'hooks/useToast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bookActions, selectBookingInfo } from 'store/slices/book';
import { useAppDispatch, useAppSelector } from 'store/useAppStore';
import { calcProperDate } from 'utils/calcProperDate';
import { getDate00 } from 'utils/dateUtil';
import { date2MonthDate } from './dateFormatter';
import { farmersBeanSeatCoordinates } from './farmersbean';
import { Banner } from 'container/common/Banner';

/**
 * 좌석 선택화면
 */
const Reservation = () => {
    const { t } = useTranslation();
    const { color, fontFamily } = useTheme();
    const toast = useToast();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { authenticated } = useStepAuthenticateManager();
    const { startDate } = useAppSelector(state => selectBookingInfo(state));

    // calendar
    const { minDate, maxDate } = calcProperDate();
    const [pickDate, setPickDate] = useState(startDate);
    const [showCalendar, setShowCalendar] = useState(false);

    // seats
    const { data: unavailableSeats } = useBookStatus(getDate00(pickDate));
    const { state, handler, action } = useSeat(pickDate, unavailableSeats);
    const { mySeatIds, isChangeSeatMode, selectedSeats } = state;
    const { setSelectedSeat, setIsChangeSeatMode, colorSelector } = action;
    const { handleChangeMode, handleOnClickSeat, handleOnChangeSeat } = handler;

    /** 날짜 선택시 */
    const handleOnClickDate = (value: Date) => {
        setPickDate(value);
        setShowCalendar(false);
        setSelectedSeat({});
    }

    /** '다음' 버튼 */
    const handleOnClickNext = () => {
        const selectedSeatIds = Object.keys(selectedSeats).map(seat => Number(seat));

        if (selectedSeatIds.length <= 0) {
            toast.notifyAlert(t('좌석을먼저선택해주세요'))
            return;
        }

        dispatch(bookActions.setBookingInfo({
            startDate: pickDate,
            seatNumber: selectedSeatIds,
        }))

        const nextPath = authenticated ? '/pay' : '/auth';
        navigate(nextPath);
    }

    /** 마이페이지 */
    const handleOnClickMy = () => {
        dispatch(bookActions.setPayInfo({ payType: 'ADDITIONAL_PAY' }));
        const nextPath = authenticated ? '/my' : '/auth';
        navigate(nextPath);
    }

    return (
        <VStack $width='100%' $minHeight='calc(var(--vh, 1vh) * 100)' $bgColor={color.mainBgColor}>
            <Header title={t('이용예약')} bgColor={color.primary900} fgColor={color.grey100} isBack isCenter
                onClickBack={() => navigate('/', { replace: true })}
                right={
                    <HStack $alignItems='center' $gap='0.5rem'>
                        <LanguagePicker color='white' />
                        <VStack onClick={handleOnClickMy} $alignItems='center' $justifyContent='center' className='pointer'>
                            <MyImage src={UserImage} alt='my page' width={30} height={30} />
                            <Text fontFamily={fontFamily.accent} color={color.grey100}>My</Text>
                        </VStack>
                    </HStack>
                } />
            <VStack $paddingBottom='0.3rem' $paddingTop='0.3rem'>
                <Banner />
            </VStack>
            <Container>
                <VStack $padding='0.5rem 0'>
                    <HStack $justifyContent='center' $width='100%'>
                        <Text as='h4' fontSize='1.2rem' fontWeight={600}>{'"대학동 파머스빈"'}</Text>
                    </HStack>
                </VStack>

                {/** Calendar */}
                <VStack $width='100%' $alignItems='center' $justifyContent='center' $position='relative' $zIndex='10'>
                    <VStack $alignItems='center' onClick={() => setShowCalendar(!showCalendar)}>
                        <Button>
                            <Text underline fontSize='1.1rem' fontWeight={700}>{t('날짜선택')}: {date2MonthDate(pickDate, t)}</Text>
                        </Button>
                    </VStack>

                    <Spacing margin='0.5rem' />
                    {showCalendar && <VStack>
                        <Box $position='absolute' $left='50%' $transform='translateX(-50%)'>
                            <MyCalendar minDate={minDate} maxDate={maxDate} defaultValue={pickDate} onChange={(value) => handleOnClickDate(value as Date)} />
                        </Box>
                    </VStack>}
                </VStack>

                {/** Seat Section */}
                <Box $overflowX='scroll' $position='relative' $padding='0.5rem 0.5rem' onClick={() => setShowCalendar(false)}>
                    <MyImage width={'768px'} maxWidth={'768px'} src={SeatPositions} alt='structures' />
                    <Box $position='absolute'>
                        {farmersBeanSeatCoordinates.map(({ x, y, number }) => {
                            const { fgColor, bgColor } = colorSelector(number);
                            return <Box key={number}
                                className='pointer'
                                onClick={() => handleOnClickSeat(number)}
                                $position='absolute' $left={`${38 * x}px`} $top={`${38 * y}px`}>
                                <SimpleSeat number={number} fgColor={fgColor} bgColor={bgColor} />
                            </Box>
                        })}
                        {/* <BreadHello /> */}
                    </Box>
                </Box>
                <Spacing margin='5rem' />

                <HStack $position='fixed' $bottom='0' $width='100%' $maxWidth='var(--app-max-width)' $bgColor={color.mainBgColor}>
                    {isChangeSeatMode
                        ?
                        /** 예약모드 */
                        <HStack $padding='1rem 1rem' $width='100%' $alignItems='center' $justifyContent='center' $gap='0.5rem'>
                            {mySeatIds.length > 0 && Object.keys(selectedSeats).length > 0 &&
                                <Box $flex={1}>
                                    <Button onClick={() => setIsChangeSeatMode(false)} $bgColor={color.focus200} $fgColor={color.grey100}
                                        $borderRadius='6px' $width='100%'
                                        $padding='1rem 0' $alignItems='center' $justifyContent='center'>
                                        <Text fontSize='1rem' fontWeight={700}>{t('취소')}</Text>
                                    </Button>
                                </Box>
                            }
                            <Box $flex={1}>
                                <Button onClick={handleOnChangeSeat} $bgColor={color.focus300} $fgColor={color.grey100}
                                    $borderRadius='6px' $width='100%'
                                    $padding='1rem 0' $alignItems='center' $justifyContent='center'>
                                    {Object.keys(selectedSeats).length > 0
                                        ? <Text fontSize='1rem' fontWeight={700}>{t('자리옮기기')}</Text>
                                        : <Text fontSize='1rem' fontWeight={700}>{t('옮기실 좌석을선택해주세요')}</Text>
                                    }
                                </Button>
                            </Box>
                        </HStack>

                        :
                        /** 변경모드 */
                        <HStack $padding='1rem 1rem' $width='100%' $alignItems='center' $justifyContent='center' $gap='0.5rem'>
                            {mySeatIds.length > 0 && Object.keys(selectedSeats).length > 0 &&
                                <Box $flex={1}>
                                    <Button onClick={handleChangeMode} $bgColor={color.secondary900} $fgColor={color.grey100}
                                        $borderRadius='6px' $width='100%'
                                        $padding='1rem 0' $alignItems='center' $justifyContent='center'>
                                        <Text fontSize='1rem' fontWeight={700}>{t('여기로옮기기')}</Text>
                                    </Button>
                                </Box>
                            }
                            <Box $flex={1}>
                                <Button onClick={handleOnClickNext} $bgColor={color.primary900} $fgColor={color.grey100}
                                    $borderRadius='6px' $width='100%'
                                    $padding='1rem 0' $alignItems='center' $justifyContent='center'>
                                    {Object.keys(selectedSeats).length > 0
                                        ? <Text fontSize='1rem' fontWeight={700}>{t('번좌석결제하기', { seatNumbers: Object.keys(selectedSeats).join(', ') })}</Text>
                                        : <Text fontSize='1rem' fontWeight={700}>{t('좌석을선택해주세요')}</Text>
                                    }
                                </Button>
                            </Box>
                        </HStack>
                    }
                </HStack>
            </Container>
        </VStack>
    );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export default Reservation;
